/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * Adapted from https://github.com/felquis/react-credit-cards-2
 * MIT License
 */

import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
    cardTypesMap,
    getCardType,
    setInitialValidCardTypes,
    validateCardNumber,
    sanitizeNumber,
} from './cardHelpers';

import './styles.scss';

const validCardTypes = setInitialValidCardTypes();

export default function CreditCard({
    number,
    issuer,
    preview = false,
    expiry,
    cvc,
    focused,
    name,
    callback,
}) {
    const purifiedNumber = sanitizeNumber(number);

    const cardOptions = useMemo(() => {
        let updatedIssuer = 'unknown';

        if (purifiedNumber) {
            const validatedIssuer = getCardType(purifiedNumber);

            if (validCardTypes.includes(validatedIssuer)) {
                updatedIssuer = validatedIssuer;
            }
        }

        let maxLength = 16;

        if (cardTypesMap.amex.includes(updatedIssuer)) {
            maxLength = 15;
        } else if (cardTypesMap.dinersclub.includes(updatedIssuer)) {
            maxLength = 14;
        } else if (['hipercard', 'mastercard', 'visa'].includes(updatedIssuer)) {
            maxLength = 19;
        }

        return {
            issuer: updatedIssuer,
            maxLength,
        };
    }, [purifiedNumber]);

    const cardIssuer = useMemo(
        () => (preview && issuer ? issuer.toLowerCase() : cardOptions.issuer),
        [cardOptions.issuer, issuer, preview]
    );

    const cardNumber = useMemo(() => {
        let maxLength = preview ? 19 : cardOptions.maxLength;
        let nextNumber =
            typeof purifiedNumber === 'number'
                ? purifiedNumber.toString()
                : String(purifiedNumber).replace(/[A-Za-z]| /g, '');

        if (Number.isNaN(parseInt(nextNumber, 10)) && !preview) {
            nextNumber = '';
        }

        if (maxLength > 16) {
            maxLength = nextNumber.length <= 16 ? 16 : maxLength;
        }

        if (nextNumber.length > maxLength) {
            nextNumber = nextNumber.slice(0, maxLength);
        }

        while (nextNumber.length < maxLength) {
            nextNumber += '•';
        }

        if (
            cardTypesMap.amex.includes(cardIssuer) ||
            cardTypesMap.dinersclub.includes(cardIssuer)
        ) {
            const format = [0, 4, 10];
            const limit = [4, 6, 5];
            nextNumber = `${nextNumber.substr(format[0], limit[0])} ${nextNumber.substr(
                format[1],
                limit[1]
            )} ${nextNumber.substr(format[2], limit[2])}`;
        } else if (nextNumber.length > 16) {
            const format = [0, 4, 8, 12];
            const limit = [4, 7];
            nextNumber = `${nextNumber.substr(format[0], limit[0])} ${nextNumber.substr(
                format[1],
                limit[0]
            )} ${nextNumber.substr(format[2], limit[0])} ${nextNumber.substr(
                format[3],
                limit[1]
            )}`;
        } else {
            for (let i = 1; i < maxLength / 4; i += 1) {
                const space_index = i * 4 + (i - 1);
                nextNumber = `${nextNumber.slice(0, space_index)} ${nextNumber.slice(
                    space_index
                )}`;
            }
        }

        return nextNumber;
    }, [cardOptions.maxLength, cardIssuer, purifiedNumber, preview]);

    const cardExpiry = useMemo(() => {
        const date = typeof expiry === 'number' ? expiry.toString() : expiry;
        let month = '';
        let year = '';

        if (date.includes('/')) {
            [month, year] = date.split('/');
        } else if (date.length) {
            month = date.substr(0, 2);
            year = date.substr(2, 6);
        }

        while (month.length < 2) {
            month += '•';
        }

        if (year.length > 2) {
            year = year.substr(2, 4);
        }

        while (year.length < 2) {
            year += '•';
        }

        return `${month}/${year}`;
    }, [expiry]);

    useEffect(() => {
        if (cardNumber !== purifiedNumber) {
            if (typeof callback === 'function') {
                callback(cardOptions, validateCardNumber(cardNumber));
            }
        }
    }, [callback, cardOptions, cardNumber, purifiedNumber]);

    return (
        <div key="Cards" className="rccs">
            <div
                className={[
                    'rccs__card',
                    `rccs__card--${cardIssuer}`,
                    focused === 'cvc' && cardIssuer !== 'american-express'
                        ? 'rccs__card--flipped'
                        : '',
                ]
                    .join(' ')
                    .trim()}
            >
                <div className="rccs__card--front">
                    <div className="rccs__card__background" />
                    <div className="rccs__issuer" />
                    <div
                        className={[
                            'rccs__cvc__front',
                            focused === 'cvc' ? 'rccs--focused' : '',
                        ]
                            .join(' ')
                            .trim()}
                    >
                        {cvc}
                    </div>
                    <div
                        className={[
                            'rccs__number',
                            cardNumber.replace(/ /g, '').length > 16
                                ? 'rccs__number--large'
                                : '',
                            focused === 'number' ? 'rccs--focused' : '',
                            cardNumber.substr(0, 1) !== '•' ? 'rccs--filled' : '',
                        ]
                            .join(' ')
                            .trim()}
                    >
                        {cardNumber}
                    </div>
                    <div
                        className={[
                            'rccs__name',
                            focused === 'name' ? 'rccs--focused' : '',
                            name ? 'rccs--filled' : '',
                        ]
                            .join(' ')
                            .trim()}
                    >
                        {name || 'YOUR NAME HERE'}
                    </div>
                    <div
                        className={[
                            'rccs__expiry',
                            focused === 'expiry' ? 'rccs--focused' : '',
                            cardExpiry.substr(0, 1) !== '•' ? 'rccs--filled' : '',
                        ]
                            .join(' ')
                            .trim()}
                    >
                        <div className="rccs__expiry__valid">valid thru</div>
                        <div className="rccs__expiry__value">{cardExpiry}</div>
                    </div>
                    <div className="rccs__chip" />
                </div>
                <div className="rccs__card--back">
                    <div className="rccs__card__background" />
                    <div className="rccs__stripe" />
                    <div className="rccs__signature" />
                    <div
                        className={['rccs__cvc', focused === 'cvc' ? 'rccs--focused' : '']
                            .join(' ')
                            .trim()}
                    >
                        {cvc}
                    </div>
                    <div className="rccs__issuer" />
                </div>
            </div>
        </div>
    );
}

CreditCard.propTypes = {
    callback: PropTypes.func,
    cvc: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    expiry: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    focused: PropTypes.string,
    issuer: PropTypes.string,
    name: PropTypes.string.isRequired,
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    preview: PropTypes.bool,
};

// cSpell:ignore dinersclub, rccs
